import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { API, getImageUrl } from '../App';
import {
  DollarSign, TrendingUp, Building2, Users, Activity, ChevronDown, ChevronUp,
  Search, Wallet, PieChart, ArrowRight, Clock, CheckCircle, XCircle, Loader2, Receipt
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RPieChart, Pie, Cell, Legend } from 'recharts';

const adminAxios = axios.create();
adminAxios.interceptors.request.use(config => {
  const token = localStorage.getItem('admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const fmtBRL = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444', '#06b6d4'];

const StatusBadge = ({ status }) => {
  const map = {
    ativo: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: <CheckCircle className="w-3 h-3" /> },
    expirado: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-3 h-3" /> },
    pago: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: <CheckCircle className="w-3 h-3" /> },
    pendente: { bg: 'bg-amber-100', text: 'text-amber-700', icon: <Clock className="w-3 h-3" /> },
    reservado: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <Wallet className="w-3 h-3" /> },
    sem_vinculo: { bg: 'bg-slate-100', text: 'text-slate-500', icon: <XCircle className="w-3 h-3" /> },
  };
  const s = map[status] || map.pendente;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.bg} ${s.text}`}>{s.icon}{status.replace('_', ' ').toUpperCase()}</span>;
};

// ==================== TAB: OVERVIEW ====================
const OverviewTab = ({ overview, planRules }) => {
  if (!overview) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;

  const cards = [
    { label: 'Receita Total', value: fmtBRL(overview.total_revenue), icon: <DollarSign className="w-5 h-5" />, color: 'emerald' },
    { label: 'Repasse Academias (70%)', value: fmtBRL(overview.total_gym_share_70), icon: <Building2 className="w-5 h-5" />, color: 'blue' },
    { label: 'Reserva Check-in (30%)', value: fmtBRL(overview.total_checkin_reserve_30), icon: <Wallet className="w-5 h-5" />, color: 'violet' },
    { label: 'Check-in Utilizado', value: fmtBRL(overview.total_checkin_used), icon: <Activity className="w-5 h-5" />, color: 'amber' },
    { label: 'Saldo Disponível', value: fmtBRL(overview.total_checkin_remaining), icon: <TrendingUp className="w-5 h-5" />, color: 'sky' },
    { label: 'Vínculos Ativos', value: overview.active_bindings, icon: <Users className="w-5 h-5" />, color: 'orange' },
  ];
  const cMap = { emerald: 'bg-emerald-50 text-emerald-600', blue: 'bg-blue-50 text-blue-600', violet: 'bg-violet-50 text-violet-600', amber: 'bg-amber-50 text-amber-600', sky: 'bg-sky-50 text-sky-600', orange: 'bg-orange-50 text-orange-600' };

  const utilPct = overview.total_checkin_reserve_30 > 0 ? Math.round((overview.total_checkin_used / overview.total_checkin_reserve_30) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {cards.map(c => (
          <Card key={c.label} className="border-slate-100">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cMap[c.color]}`}>{c.icon}</div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase tracking-wider">{c.label}</p>
                  <p className="text-lg font-bold text-slate-900" data-testid={`fin-${c.label.replace(/\s/g, '-').toLowerCase()}`}>{c.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Utilization bar */}
      <Card className="border-slate-100">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-700">Utilização do Saldo de Check-in</p>
            <span className="text-sm font-bold text-slate-900">{utilPct}%</span>
          </div>
          <Progress value={utilPct} className={`h-3 ${utilPct > 80 ? '[&>div]:bg-red-500' : utilPct > 50 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'} bg-slate-100 rounded-full`} />
          <div className="flex justify-between mt-2 text-xs text-slate-500">
            <span>Utilizado: {fmtBRL(overview.total_checkin_used)}</span>
            <span>Restante: {fmtBRL(overview.total_checkin_remaining)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pie: 70/30 split */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-sm font-bold">Distribuição 70/30</CardTitle></CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <RPieChart>
                  <Pie data={[{ name: 'Academias (70%)', value: overview.total_gym_share_70 }, { name: 'Reserva CK (30%)', value: overview.total_checkin_reserve_30 }]} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    <Cell fill="#3b82f6" /><Cell fill="#8b5cf6" />
                  </Pie>
                  <Tooltip formatter={(v) => fmtBRL(v)} />
                </RPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Bar: Plans distribution */}
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-sm font-bold">Alunos por Plano</CardTitle></CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.plan_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="plan" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} name="Alunos" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Plan rules table */}
      {planRules && (
        <Card className="border-slate-100">
          <CardHeader><CardTitle className="text-sm font-bold">Regras de Pagamento por Plano</CardTitle></CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Plano</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Preço</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Academia (70%)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Reserva CK (30%)</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">CKs R$10</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Max CKs</th>
                  </tr>
                </thead>
                <tbody>
                  {planRules.map(r => (
                    <tr key={r.plan_key} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-3 font-semibold text-slate-900">{r.plan_name}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{fmtBRL(r.plan_price)}</td>
                      <td className="px-4 py-3 text-right text-blue-600 font-medium">{fmtBRL(r.gym_share_70)}</td>
                      <td className="px-4 py-3 text-right text-violet-600 font-medium">{fmtBRL(r.checkin_reserve_30)}</td>
                      <td className="px-4 py-3 text-center">{r.fixed_limit}</td>
                      <td className="px-4 py-3 text-center font-bold">{r.max_checkins}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== TAB: STUDENTS ====================
const StudentsTab = ({ students }) => {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const filtered = (students || []).filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) || s.gym_name?.toLowerCase().includes(search.toLowerCase())
  );

  const loadDetail = async (sid) => {
    if (expanded === sid) { setExpanded(null); return; }
    setExpanded(sid);
    setLoadingDetail(true);
    try {
      const res = await adminAxios.get(`${API}/admin/financial/students/${sid}`);
      setDetail(res.data);
    } catch { setDetail(null); }
    setLoadingDetail(false);
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar aluno ou academia..." className="pl-10" />
      </div>
      {filtered.length === 0 ? (
        <p className="text-center py-8 text-slate-400">Nenhum aluno com plano encontrado</p>
      ) : filtered.map(s => (
        <Card key={s.student_id} className="border-slate-100" data-testid={`fin-student-${s.student_id}`}>
          <CardContent className="p-0">
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50" onClick={() => loadDetail(s.student_id)}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {s.profile_photo ? (
                  <img src={getImageUrl(s.profile_photo)} alt="" className="w-10 h-10 rounded-full object-cover border border-slate-200" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-500">{s.name?.charAt(0)}</div>
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{s.name}</p>
                  <p className="text-[11px] text-slate-500">{s.plan_name} | {s.gym_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-slate-500">Saldo CK</p>
                  <p className="text-sm font-bold text-slate-900">{fmtBRL(s.checkin_remaining)}</p>
                </div>
                <StatusBadge status={s.binding_status} />
                {expanded === s.student_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
            </div>
            {expanded === s.student_id && (
              <div className="border-t border-slate-100 p-4 bg-slate-50/50 space-y-4">
                {loadingDetail ? (
                  <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                ) : detail ? (
                  <>
                    {/* Financial cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-[10px] text-slate-500 uppercase">Plano</p>
                        <p className="text-sm font-bold text-slate-900">{fmtBRL(detail.plan_price)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-blue-200">
                        <p className="text-[10px] text-blue-500 uppercase">Academia (70%)</p>
                        <p className="text-sm font-bold text-blue-700">{fmtBRL(detail.gym_share_70)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-violet-200">
                        <p className="text-[10px] text-violet-500 uppercase">Reserva CK (30%)</p>
                        <p className="text-sm font-bold text-violet-700">{fmtBRL(detail.checkin_reserve_30)}</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 border border-emerald-200">
                        <p className="text-[10px] text-emerald-500 uppercase">Saldo Restante</p>
                        <p className="text-sm font-bold text-emerald-700">{fmtBRL(detail.checkin_remaining)}</p>
                      </div>
                    </div>

                    {/* Utilization */}
                    <div>
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>{detail.checkin_count}/{detail.max_checkins} check-ins ({detail.utilization_pct}%)</span>
                        <span>{fmtBRL(detail.value_per_checkin)}/ck</span>
                      </div>
                      <Progress value={detail.utilization_pct} className="h-2 [&>div]:bg-violet-500 bg-slate-100" />
                    </div>

                    {/* Binding info */}
                    {detail.binding && (
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Vinculo</p>
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <p className="text-sm font-bold text-slate-900">{detail.binding.gym_name}</p>
                            <p className="text-xs text-slate-500">Repasse: {fmtBRL(detail.binding.gym_share_70)}</p>
                          </div>
                          <div className="text-right">
                            <StatusBadge status={detail.binding.status} />
                            <p className="text-xs text-slate-500 mt-1">{detail.binding.days_remaining}d restantes</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Checkin history */}
                    {detail.history?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Historico de Check-ins</p>
                        <div className="space-y-1.5 max-h-48 overflow-y-auto">
                          {detail.history.map((h, i) => (
                            <div key={h.checkin_id || h.timestamp || i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-100 text-xs">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">#{h.checkin_number}</span>
                                <div>
                                  <p className="font-medium text-slate-800">{h.gym_name}</p>
                                  <p className="text-slate-400">{h.timestamp ? new Date(h.timestamp).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'} {h.modality && `| ${h.modality}`}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-bold text-slate-900">{fmtBRL(h.value_per_checkin)}</p>
                                <StatusBadge status={h.status} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : <p className="text-sm text-slate-400">Erro ao carregar detalhes</p>}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ==================== TAB: GYMS ====================
const GymsTab = ({ gyms }) => {
  const [search, setSearch] = useState('');
  const filtered = (gyms || []).filter(g => g.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar academia..." className="pl-10" />
      </div>
      {filtered.length === 0 ? (
        <p className="text-center py-8 text-slate-400">Nenhuma academia encontrada</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Academia</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Alunos</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Ativos</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Repasse (70%)</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Check-ins</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => (
                <tr key={g.gym_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`fin-gym-${g.gym_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {g.photo_path ? (
                        <img src={getImageUrl(g.photo_path)} alt="" className="w-8 h-8 rounded-lg object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center"><Building2 className="w-4 h-4 text-orange-500" /></div>
                      )}
                      <span className="font-medium text-slate-900">{g.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-slate-700">{g.bound_students}</td>
                  <td className="px-4 py-3 text-center"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">{g.active_bindings}</span></td>
                  <td className="px-4 py-3 text-right font-bold text-blue-700">{fmtBRL(g.total_gym_share_70)}</td>
                  <td className="px-4 py-3 text-center text-slate-700">{g.checkin_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================
const AdminFinancial = () => {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [planRules, setPlanRules] = useState(null);
  const [students, setStudents] = useState(null);
  const [gyms, setGyms] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, rulesRes] = await Promise.all([
        adminAxios.get(`${API}/admin/financial/overview`),
        adminAxios.get(`${API}/admin/financial/plan-rules`),
      ]);
      setOverview(ovRes.data);
      setPlanRules(rulesRes.data);
    } catch (e) { console.error('Failed to load financial data:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const loadStudents = async () => {
    if (students) return;
    try { const r = await adminAxios.get(`${API}/admin/financial/students`); setStudents(r.data); } catch (e) { console.error('Failed to load students:', e); }
  };
  const loadGyms = async () => {
    if (gyms) return;
    try { const r = await adminAxios.get(`${API}/admin/financial/gyms`); setGyms(r.data); } catch (e) { console.error('Failed to load gyms:', e); }
  };

  const handleTab = (t) => {
    setTab(t);
    if (t === 'students') loadStudents();
    if (t === 'gyms') loadGyms();
  };

  const tabs = [
    { id: 'overview', label: 'Visao Geral', icon: <PieChart className="w-4 h-4" /> },
    { id: 'students', label: 'Por Aluno', icon: <Users className="w-4 h-4" /> },
    { id: 'gyms', label: 'Por Academia', icon: <Building2 className="w-4 h-4" /> },
  ];

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-10 h-10 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-financial">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Financeiro</h1>
          <p className="text-slate-500 text-sm mt-0.5">Calculos automaticos de vinculo, check-in e distribuicao</p>
        </div>
        <Button variant="outline" onClick={fetchData} className="gap-2"><Receipt className="w-4 h-4" />Atualizar</Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
        {tabs.map(t => (
          <button key={t.id} onClick={() => handleTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${tab === t.id ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`fin-tab-${t.id}`}
          >
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && <OverviewTab overview={overview} planRules={planRules} />}
      {tab === 'students' && <StudentsTab students={students} />}
      {tab === 'gyms' && <GymsTab gyms={gyms} />}
    </div>
  );
};

export default AdminFinancial;
